import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { VesselsAPI, VesselPayload, VesselCategory } from '../services/vessels.ts';
import api from '../services/api.ts';
import { applyCurrencyMask, parseCurrency } from '../utils/currency.ts';
import { ArrowLeftIcon, DocumentTextIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';
import FileUpload from '../components/FileUpload.tsx';

const CreateVessel: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<VesselPayload>({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    registrationNumber: '',
    color: '',
    purchasePrice: 0,
    sellingPrice: 0,
    purchaseDate: new Date().toISOString().split('T')[0],
    category: 'boat',
  });

  const [purchasePriceDisplay, setPurchasePriceDisplay] = useState<string>('');
  const [sellingPriceDisplay, setSellingPriceDisplay] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation(
    async () => {
      let uploadedFiles = form.attachments || [];
      
      // Upload new files if any
      if (uploadedFiles.length > 0) {
        const formData = new FormData();
        const filesToUpload = uploadedFiles.filter((file: any) => file.file instanceof File);
        
        if (filesToUpload.length > 0) {
          filesToUpload.forEach((attachment: any) => {
            formData.append('files', attachment.file);
          });

          const uploadResponse = await api.post('/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          if (uploadResponse.data.success) {
            const newUploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
              id: Date.now() + index,
              name: file.originalName,
              filename: file.filename,
              url: file.url,
              size: file.size,
              mimetype: file.mimetype,
              type: filesToUpload[index].type,
              category: filesToUpload[index].category,
            }));
            
            // Merge existing (if any, though in create usually none) with new
            uploadedFiles = newUploadedFiles;
          }
        }
      }

      const payload: VesselPayload = {
        ...form,
        purchasePrice: form.purchasePrice,
        sellingPrice: form.sellingPrice || null,
        attachments: uploadedFiles,
      };
      return VesselsAPI.create(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vessels']);
        navigate('/vessels');
      },
      onError: err => {
        console.error('Erro ao criar embarcação:', err);
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'year' ? Number(value) : value,
    }) as any);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyChange = (field: 'purchasePrice' | 'sellingPrice', raw: string) => {
    const masked = applyCurrencyMask(raw);
    const numeric = parseCurrency(masked);
    if (field === 'purchasePrice') setPurchasePriceDisplay(masked);
    else setSellingPriceDisplay(masked);
    setForm(prev => ({ ...prev, [field]: numeric }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.make?.trim()) next.make = 'Marca é obrigatória';
    if (!form.model?.trim()) next.model = 'Modelo é obrigatório';
    if (!form.registrationNumber?.trim()) next.registrationNumber = 'Registro é obrigatório';
    if (!form.color?.trim()) next.color = 'Cor é obrigatória';
    if (!form.year || form.year < 1900) next.year = 'Ano inválido';
    if (!form.purchaseDate) next.purchaseDate = 'Data de compra é obrigatória';
    if (!form.category) next.category = 'Categoria é obrigatória';
    if (!form.purchasePrice || form.purchasePrice <= 0) next.purchasePrice = 'Valor de compra deve ser maior que zero';
    // if (form.sellingPrice !== undefined && form.sellingPrice < 0) next.sellingPrice = 'Valor de venda não pode ser negativo';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/vessels')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Embarcações
          </button>

          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BoatIcon className="h-8 w-8 mr-3 text-primary-600" />
            Criar Nova Embarcação
          </h1>
          <p className="text-gray-600 mt-2">Adicione uma nova embarcação ao seu patrimônio</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marca *</label>
                <input
                  name="make"
                  value={form.make}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.make ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.make && <p className="mt-1 text-sm text-red-600">{errors.make}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modelo *</label>
                <input
                  name="model"
                  value={form.model}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.model ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.model && <p className="mt-1 text-sm text-red-600">{errors.model}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ano *</label>
                <input
                  type="number"
                  name="year"
                  value={form.year}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.year ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Registro *</label>
                <input
                  name="registrationNumber"
                  value={form.registrationNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.registrationNumber && <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor *</label>
                <input
                  name="color"
                  value={form.color}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.color ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.color && <p className="mt-1 text-sm text-red-600">{errors.color}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
                <select
                  name="category"
                  value={form.category as VesselCategory}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="boat">Barco</option>
                  <option value="yacht">Iate</option>
                  <option value="sailboat">Veleiro</option>
                  <option value="other">Outro</option>
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de Compra *</label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={form.purchaseDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.purchaseDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.purchaseDate && <p className="mt-1 text-sm text-red-600">{errors.purchaseDate}</p>}
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
              Valores
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor de Compra *</label>
                <input
                  type="text"
                  name="purchasePrice"
                  value={purchasePriceDisplay}
                  onChange={e => handleCurrencyChange('purchasePrice', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.purchasePrice ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
                {errors.purchasePrice && <p className="mt-1 text-sm text-red-600">{errors.purchasePrice}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor de Venda</label>
                <input
                  type="text"
                  name="sellingPrice"
                  value={sellingPriceDisplay}
                  onChange={e => handleCurrencyChange('sellingPrice', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.sellingPrice ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
                {errors.sellingPrice && <p className="mt-1 text-sm text-red-600">{errors.sellingPrice}</p>}
              </div>
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos da embarcação e documentos como registro, nota fiscal, manual, etc.
            </p>
            <FileUpload
              files={form.attachments || []}
              onFilesChange={files => setForm(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/vessels')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {createMutation.isLoading ? 'Salvando...' : 'Criar Embarcação'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVessel;